/**
 * The chart visualisations: line, area, column (vertical bars), horizontal
 * bars, stacked bars, donut, pie, gauge and sparkline — AgencyAnalytics'
 * visualisation set, drawn from one query result.
 */

import type { ReactNode } from 'react';

import { BarChart, LineChart, seriesTone, Sparkline } from '../charts.tsx';
import { useChangeFormat } from './change-format.ts';
import { Donut, HorizontalBars } from './charts-extra.tsx';
import { bucketLabel, formatChange, formatValue } from './format.ts';
import type { CatalogueMetric, Widget, WidgetResult } from './reports-api.ts';

const GAUGE_WIDTH = 220;
const GAUGE_HEIGHT = 124;
const GAUGE_RADIUS = 92;

/** A half-circle dial: how far the value has come towards its target. */
export function GaugeBody({
  widget,
  result,
  metric,
}: {
  widget: Widget;
  result: WidgetResult;
  metric: CatalogueMetric;
}): ReactNode {
  const value = result.metrics.find((item) => item.key === metric.key)?.value ?? null;
  const target = widget.data.target ?? 0;
  const share = target > 0 && value !== null ? Math.max(0, Math.min(1, value / target)) : 0;
  const cx = GAUGE_WIDTH / 2;
  const cy = GAUGE_HEIGHT - 12;
  const arc = (fraction: number) => {
    const angle = Math.PI * (1 - fraction);
    return `${cx + GAUGE_RADIUS * Math.cos(angle)},${cy - GAUGE_RADIUS * Math.sin(angle)}`;
  };
  return (
    <div className="rw-gauge">
      <svg
        viewBox={`0 0 ${GAUGE_WIDTH} ${GAUGE_HEIGHT}`}
        role="img"
        aria-label={`${formatValue(value, metric.format)} of ${formatValue(target, metric.format)}`}
      >
        <path
          d={`M${arc(0)} A${GAUGE_RADIUS},${GAUGE_RADIUS} 0 0 1 ${arc(1)}`}
          className="rw-gauge-track"
        />
        {share > 0 && (
          <path
            d={`M${arc(0)} A${GAUGE_RADIUS},${GAUGE_RADIUS} 0 0 1 ${arc(share)}`}
            className="rw-gauge-fill"
          />
        )}
        <text x={cx} y={cy - 18} textAnchor="middle" className="rw-gauge-value">
          {formatValue(value, metric.format, true)}
        </text>
      </svg>
      <div className="rw-goal-foot">
        {target > 0
          ? `${Math.round(share * 100)}% of ${formatValue(target, metric.format)}`
          : 'Set a target in Display'}
      </div>
    </div>
  );
}

/** One number with its change, over a trend line that fills the widget. */
export function SparklineBody({
  result,
  metric,
}: {
  result: WidgetResult;
  metric: CatalogueMetric;
}): ReactNode {
  const entry = result.metrics.find((item) => item.key === metric.key);
  const change = formatChange(entry?.change ?? null, metric.higher_is_better !== false, {
    as: useChangeFormat(),
    format: metric.format,
  });
  return (
    <div className="rw-spark">
      <div className="rw-spark-head">
        <span className="rw-metric-value mono">
          {formatValue(entry?.value ?? null, metric.format)}
        </span>
        {change && <span className={`rw-change rw-change-${change.tone}`}>{change.text}</span>}
      </div>
      <Sparkline
        values={result.series.map((point) => point.values[metric.key] ?? 0)}
        width={300}
        height={64}
      />
    </div>
  );
}

export function ChartBody({
  widget,
  result,
  metrics,
  height,
}: {
  widget: Widget;
  result: WidgetResult;
  metrics: CatalogueMetric[];
  height: number;
}): ReactNode {
  const first = metrics[0] as CatalogueMetric;
  const format = (value: number) => formatValue(value, first.format, true);
  const slices = () =>
    (result.rows ?? []).map((row, index) => ({
      label: row.label,
      value: row.values[first.key] ?? 0,
      tone: seriesTone(index),
    }));
  if (widget.type === 'DONUT' || widget.type === 'PIE') {
    return (
      <Donut
        slices={slices()}
        height={height}
        ring={widget.type === 'DONUT'}
        format={(value) => formatValue(value, first.format)}
      />
    );
  }
  if ((widget.type === 'HORIZONTAL_BAR' || widget.type === 'STACKED_BAR') && result.rows) {
    return (
      <HorizontalBars
        rows={result.rows}
        metrics={metrics}
        stacked={widget.type === 'STACKED_BAR'}
        format={(value, metric) => formatValue(value, metric.format, true)}
      />
    );
  }
  // Column: one bar per dimension value, or per day/week/month when there is no dimension.
  if (widget.type === 'BAR' && result.rows) {
    return (
      <BarChart
        points={result.rows.map((row) => ({ label: row.label, value: row.values[first.key] ?? 0 }))}
        height={height}
        formatValue={format}
      />
    );
  }
  const points = (key: string, series = result.series) =>
    series.map((point) => ({
      label: bucketLabel(point.bucket, result.granularity),
      value: point.values[key] ?? 0,
    }));
  if (widget.type === 'BAR' || widget.type === 'HORIZONTAL_BAR')
    return <BarChart points={points(first.key)} height={height} formatValue={format} />;
  const series = metrics
    .slice(0, first.format === metrics[1]?.format ? 2 : 1)
    .map((metric, index) => ({ name: metric.label, points: points(metric.key), tone: index }));
  if (result.compare_series && widget.display.show_comparison !== false) {
    series.push({
      name: `${first.label} (previous)`,
      points: points(first.key, result.compare_series).map((point, index) => ({
        ...point,
        label: series[0]?.points[index]?.label ?? point.label,
      })),
      tone: 5,
    });
  }
  return (
    <LineChart series={series} height={height} formatValue={format} area={widget.type === 'AREA'} />
  );
}
